/**
 * 기구 무대 장면(machine-stage · PICNIC-SB-FIELD-001 §6.1 · 물품 목록 4.8.1 · 2026-10-06) — 장면 키 → 배경 테마(데이터만 · three 없음).
 * 그리기는 ./backdrop(하늘 돔 · 바닥 · 원경 실루엣 · 저폴리 소품 · 이모지 손님 · 축하 효과).
 * 좌표: m · 무대 가운데(받침대) = 원점 · 카메라는 +z 쪽에서 −z 를 본다(기계 앞). 소품은 대개 뒤(−z)와 옆에 둔다.
 */

import type { CharacterKind, ItemKind } from "./characters";

export type PropKind =
  | "tree"
  | "pine"
  | "palm"
  | "cactus"
  | "rock"
  | "bush"
  | "flowers"
  | "mushroom"
  | "balloons"
  | "flag"
  | "bunting"
  | "lamp"
  | "fence"
  | "tent"
  | "column"
  | "tower"
  | "ship"
  | "cake"
  | "helipad"
  | "nest"
  | "mailbox"
  | "rainbow"
  | "stand"
  | "swingset";

export interface PropSpec {
  readonly kind: PropKind;
  /** 바닥 자리 [x, z](m) */
  readonly at: readonly [number, number];
  readonly scale?: number;
  /** 주 색(종류마다 뜻이 다르다 — 꽃 · 풍선 · 천막 줄무늬) */
  readonly color?: string;
  /** y 축 회전(도) */
  readonly rot?: number;
  /** bunting — 끝점 [x, z] */
  readonly to?: readonly [number, number];
}

export interface FigureSpec {
  /** 3D 캐릭터(./characters) — char 또는 item 하나 */
  readonly char?: CharacterKind;
  readonly item?: ItemKind;
  /** 캐릭터 몸(옷) 색 */
  readonly color?: string;
  /** [x, y, z](m) — y 는 발밑 */
  readonly at: readonly [number, number, number];
  /** 높이(m) */
  readonly size: number;
  /** 가만히 있을 때 몸짓 — bob 위아래 · sway 좌우 · fly 날기 · none */
  readonly idle?: "bob" | "sway" | "fly" | "none";
  /** 바라볼 바닥 점 [x, z](m) — 없으면 무대 가운데와 카메라(+z) 사이 */
  readonly face?: readonly [number, number];
  /** 별을 받을 때 반응하는 손님(기본 true) */
  readonly cheers?: boolean;
}

/** 운영자가 올린 3D 모델(2026-10-08 · 카탈로그 stage_model) 놓기 — 파일은 화면이 stageModelUrl 로 준다 */
export interface StageModelSpec {
  /** 카탈로그 stage_model 키 */
  readonly model: string;
  /** [x, y, z](m) — y 는 발밑 */
  readonly at: readonly [number, number, number];
  /** 높이(m) — 모델 경계 상자 높이를 이 값에 맞춘다 */
  readonly size: number;
  /** y 축 회전(도) */
  readonly rot?: number;
}

export type FarKind =
  "mountains" | "hills" | "forest" | "city" | "dunes" | "islands" | "fair" | "wall";

export interface FarLayer {
  readonly kind: FarKind;
  readonly color: string;
  /** 반지름(m) · 높이(m) */
  readonly dist: number;
  readonly height: number;
  readonly seed: number;
  /** 눈 덮인 봉우리(mountains) */
  readonly snow?: boolean;
  /** 창문 불빛(city) */
  readonly lights?: string;
}

export type GroundPattern = "grass" | "sand" | "water" | "tile" | "wood" | "rock" | "moss";

export interface StageTheme {
  readonly key: string;
  /** 하늘 위 · 지평선 · 아래(실내는 벽 색) */
  readonly sky: { readonly top: string; readonly horizon: string; readonly bottom: string };
  readonly sun?: {
    readonly elev: number;
    readonly azim: number;
    readonly color: string;
    readonly size: number;
  };
  readonly stars?: boolean;
  readonly clouds?: { readonly count: number; readonly color: string };
  readonly ground: {
    readonly color: string;
    readonly color2: string;
    readonly pattern: GroundPattern;
  };
  /** 무대 판(받침대 둘레 바닥) 색 */
  readonly deck: string;
  /** 기계가 서는 받침대 꾸밈 — 몸통 · 윗면 깔개 · 깔개 무늬 · 꼬마전구 · 장식 천(두 색) */
  readonly podium: {
    readonly side: string;
    readonly top: string;
    readonly dots: string;
    readonly bulbs: readonly [string, string];
    readonly skirt: readonly [string, string];
    readonly pattern: "dots" | "stars" | "hearts" | "planks";
  };
  readonly far: readonly FarLayer[];
  readonly props: readonly PropSpec[];
  readonly figures: readonly FigureSpec[];
  readonly light: {
    readonly sky: string;
    readonly ground: string;
    readonly hemi: number;
    readonly key: string;
    readonly keyI: number;
  };
  readonly fog: { readonly color: string; readonly near: number; readonly far: number };
  /** 축하 조각 색(별 · 하트 · 색종이 — 별을 받으면) */
  readonly confetti: readonly string[];
  /** 바닥 원판 · 하늘 돔 크기 배율(기본 1 — 반지름 42 · 45m). 넓은 바닥(우리 반 룸)은 키운다 */
  readonly span?: number;
  /**
   * 정지 장면 — 구름 · 손님 몸짓 · 움직이는 소품 없이 모두 한 번에 구워(재질 갈래마다 메시 하나) CPU · 그리기 호출을 아낀다.
   * 기계가 많은 우리 반 룸(최대 30대)용 · 손님 반응(react)도 없다
   */
  readonly still?: boolean;
  /** 실내(하늘 대신 벽 — 해 · 구름 없음) */
  readonly indoor?: boolean;
  /** 운영자가 올린 3D 모델(무대 꾸밈 데이터에서만 — 기본 장면에는 없다 · course 가 따로 불러 놓는다) */
  readonly models?: readonly StageModelSpec[];
}

const DAY = { top: "#4FA3E8", horizon: "#CDEBFF", bottom: "#E9F6FF" };
const PARTY = ["#FFD23F", "#FF5A8A", "#4FB3E8", "#7BD389", "#C77DFF"];

/** 놀이공원 공통 원경 */
const FAIR_FAR: readonly FarLayer[] = [
  { kind: "hills", color: "#8FC79A", dist: 26, height: 3.2, seed: 3 },
  { kind: "fair", color: "#7A8FB8", dist: 19, height: 5.5, seed: 7 },
];

export const STAGE_THEMES: Readonly<Record<string, StageTheme>> = {
  "playground-sand": {
    key: "playground-sand",
    sky: DAY,
    sun: { elev: 52, azim: -35, color: "#FFF4C2", size: 1.2 },
    clouds: { count: 9, color: "#FFFFFF" },
    ground: { color: "#7CC46A", color2: "#5FAE52", pattern: "grass" },
    deck: "#E8CF94",
    podium: {
      side: "#4FA9DF",
      top: "#7FD3F7",
      dots: "#FFFFFF",
      bulbs: ["#FFD166", "#FFFFFF"],
      skirt: ["#FFD166", "#FF8FAB"],
      pattern: "dots",
    },
    far: [
      { kind: "hills", color: "#9ED48C", dist: 24, height: 2.6, seed: 11 },
      { kind: "forest", color: "#4E9A5B", dist: 17, height: 2.4, seed: 5 },
    ],
    props: [
      { kind: "tree", at: [-1.42, -1.6], scale: 1.3 },
      { kind: "tree", at: [1.58, -2.2], scale: 1.6 },
      { kind: "swingset", at: [-1.65, 0.2], rot: 80 },
      { kind: "bush", at: [1.12, -0.9] },
      { kind: "flowers", at: [0.68, -1.4], color: "#FF7AA8" },
      { kind: "flowers", at: [-0.75, -1.2], color: "#FFD23F" },
      { kind: "balloons", at: [1.42, -0.6], color: "#FF6B6B" },
      { kind: "fence", at: [0.0, -2.6], scale: 2 },
    ],
    figures: [
      { char: "teddy", at: [-0.42, 0.06, 0.05], size: 0.18, idle: "bob" },
      { char: "bear", at: [0.42, 0.06, 0.05], size: 0.18, idle: "bob" },
      { char: "girl", at: [-0.95, 0, -0.4], size: 0.3, idle: "sway" },
      { char: "kid", at: [0.95, 0, -0.45], size: 0.3, idle: "sway" },
    ],
    light: { sky: "#FFFFFF", ground: "#8FBF7A", hemi: 1.5, key: "#FFF1D6", keyI: 1.3 },
    fog: { color: "#CDEBFF", near: 12, far: 34 },
    confetti: PARTY,
  },
  "forest-post": {
    key: "forest-post",
    sky: { top: "#5DB0D6", horizon: "#D8F0E2", bottom: "#E8F7EE" },
    sun: { elev: 40, azim: 30, color: "#FFF6D0", size: 1 },
    clouds: { count: 6, color: "#FFFFFF" },
    ground: { color: "#5E9E4F", color2: "#4C8742", pattern: "moss" },
    deck: "#C9A876",
    podium: {
      side: "#5CB88A",
      top: "#A8E6CF",
      dots: "#FFFFFF",
      bulbs: ["#FFD166", "#FF8FB1"],
      skirt: ["#FF8FB1", "#FFFFFF"],
      pattern: "hearts",
    },
    far: [
      { kind: "mountains", color: "#7FA9A0", dist: 27, height: 6, seed: 2 },
      { kind: "forest", color: "#2F6B45", dist: 15, height: 3.6, seed: 9 },
    ],
    props: [
      { kind: "pine", at: [-1.27, -1.5], scale: 1.5 },
      { kind: "pine", at: [-1.95, -0.4], scale: 1.9 },
      { kind: "pine", at: [1.72, -1.8], scale: 1.7 },
      { kind: "mailbox", at: [0.83, -0.8] },
      { kind: "mushroom", at: [-0.68, -0.7], color: "#E5483B" },
      { kind: "mushroom", at: [1.2, 0.3], scale: 0.7, color: "#F08A24" },
      { kind: "rock", at: [-1.2, -0.6] },
      { kind: "flowers", at: [0.38, -1.3], color: "#B985F2" },
    ],
    figures: [
      { char: "fairy", at: [0.55, 0.32, 0.1], size: 0.22, idle: "fly" },
      { item: "letter", at: [-0.5, 0.25, 0.15], size: 0.12, idle: "fly", cheers: false },
      { char: "owl", at: [-1.7, 1.0, -1.45], size: 0.22, idle: "none" },
      { char: "squirrel", at: [1.3, 0, -0.35], size: 0.2, idle: "bob" },
    ],
    light: { sky: "#F4FFF6", ground: "#5E8F4E", hemi: 1.4, key: "#FFF4DA", keyI: 1.1 },
    fog: { color: "#D8F0E2", near: 9, far: 30 },
    confetti: ["#FFD23F", "#FF8FB1", "#FFFFFF", "#B985F2", "#7FD3F7"],
  },
  "amusement-day": {
    key: "amusement-day",
    sky: DAY,
    sun: { elev: 58, azim: -20, color: "#FFF6C8", size: 1.2 },
    clouds: { count: 8, color: "#FFFFFF" },
    ground: { color: "#E6D3A8", color2: "#D9C08C", pattern: "tile" },
    deck: "#F2B8C6",
    podium: {
      side: "#E8414F",
      top: "#FF8FAB",
      dots: "#FFFFFF",
      bulbs: ["#FFD166", "#FFFFFF"],
      skirt: ["#E8414F", "#FFFFFF"],
      pattern: "stars",
    },
    far: FAIR_FAR,
    props: [
      { kind: "tent", at: [-1.8, -2.0], scale: 1.4, color: "#E8414F" },
      { kind: "tent", at: [1.95, -2.4], scale: 1.1, color: "#3C8DDB" },
      { kind: "bunting", at: [-1.8, -1.0], to: [1.8, -1.0] },
      { kind: "balloons", at: [1.2, -0.5], color: "#FFC93C" },
      { kind: "balloons", at: [-1.27, -0.4], color: "#7BD389" },
      { kind: "lamp", at: [-0.9, -1.6] },
      { kind: "lamp", at: [0.9, -1.6] },
      { kind: "stand", at: [1.5, -0.6], color: "#FF8FAB" },
    ],
    figures: [
      { char: "girl", at: [-0.9, 0, -0.4], size: 0.3, idle: "sway" },
      { char: "kid", at: [-0.6, 0, -0.6], size: 0.3, idle: "sway" },
      { char: "kid", color: "#7BD389", at: [0.7, 0, -0.45], size: 0.28, idle: "bob" },
      { char: "adult", color: "#FF8FB1", at: [1.0, 0, -0.6], size: 0.34, idle: "sway" },
      { item: "icecream", at: [2.0, 0.55, -0.6], size: 0.16, idle: "none", cheers: false },
    ],
    light: { sky: "#FFFFFF", ground: "#C9B48A", hemi: 1.5, key: "#FFF1D6", keyI: 1.3 },
    fog: { color: "#CDEBFF", near: 12, far: 36 },
    confetti: PARTY,
  },
  "teacup-park": {
    key: "teacup-park",
    sky: { top: "#7FB8F0", horizon: "#FFE3F0", bottom: "#FFF2F8" },
    sun: { elev: 48, azim: 25, color: "#FFF4D6", size: 1.1 },
    clouds: { count: 10, color: "#FFF7FB" },
    ground: { color: "#F3DDE8", color2: "#E8C6D6", pattern: "tile" },
    deck: "#BFE6E0",
    podium: {
      side: "#9B7FE0",
      top: "#C9B6F2",
      dots: "#FFFFFF",
      bulbs: ["#FFE08A", "#FFFFFF"],
      skirt: ["#FF9EC4", "#FFFFFF"],
      pattern: "hearts",
    },
    far: FAIR_FAR,
    props: [
      { kind: "tent", at: [-1.72, -2.1], scale: 1.3, color: "#B57CE8" },
      { kind: "bunting", at: [-1.9, -1.2], to: [1.9, -1.2] },
      { kind: "flowers", at: [-0.98, -0.6], color: "#FF8FB1" },
      { kind: "flowers", at: [0.98, -0.7], color: "#FFD166" },
      { kind: "balloons", at: [1.5, -0.2], color: "#FF9EC4" },
      { kind: "lamp", at: [1.12, -1.8] },
      { kind: "tree", at: [1.95, -2.6], scale: 1.4, color: "#FFB7D5" },
    ],
    figures: [
      { char: "kid", color: "#7BD389", at: [-1.0, 0, -0.35], size: 0.28, idle: "bob" },
      { char: "girl", at: [-0.75, 0, -0.55], size: 0.3, idle: "sway" },
      { char: "kid", at: [-0.5, 0, -0.75], size: 0.3, idle: "sway" },
      { item: "teapot", at: [1.0, 0, -0.4], size: 0.22, idle: "none", cheers: false },
      { item: "cup", at: [0.75, 0, -0.6], size: 0.16, idle: "none", cheers: false },
    ],
    light: { sky: "#FFF8FC", ground: "#E0BFCF", hemi: 1.5, key: "#FFF1E6", keyI: 1.2 },
    fog: { color: "#FFE3F0", near: 11, far: 34 },
    confetti: ["#FF8FB1", "#FFD166", "#BFE6E0", "#C77DFF", "#FFFFFF"],
  },
  "pirate-sea": {
    key: "pirate-sea",
    sky: { top: "#2F86D6", horizon: "#BFE6FF", bottom: "#DDF2FF" },
    sun: { elev: 35, azim: -40, color: "#FFF2C0", size: 1.3 },
    clouds: { count: 7, color: "#FFFFFF" },
    ground: { color: "#2C7FC8", color2: "#4BA3E3", pattern: "water" },
    deck: "#B88A5A",
    podium: {
      side: "#8A5A3B",
      top: "#C89B6D",
      dots: "#A87B4F",
      bulbs: ["#FFD23F", "#FFF4C4"],
      skirt: ["#2B2B33", "#E8414F"],
      pattern: "planks",
    },
    far: [
      { kind: "islands", color: "#4E8E63", dist: 25, height: 3.5, seed: 4 },
      { kind: "islands", color: "#6FA77A", dist: 18, height: 2, seed: 12 },
    ],
    props: [
      { kind: "ship", at: [-1.65, -3.6], scale: 1.3, rot: 25 },
      { kind: "palm", at: [1.65, -1.6], scale: 1.3 },
      { kind: "rock", at: [1.27, -0.6], scale: 1.2 },
      { kind: "rock", at: [-1.12, -0.4], scale: 0.8 },
      { kind: "flag", at: [1.05, -1.9], color: "#2B2B33" },
    ],
    figures: [
      { char: "pirate", at: [-0.85, 0, -0.35], size: 0.34, idle: "sway" },
      { char: "parrot", at: [0.6, 0, 0.1], size: 0.2, idle: "bob" },
      { char: "gull", at: [-0.9, 1.4, -1.8], size: 0.25, idle: "fly" },
      { char: "gull", at: [1.4, 1.7, -2.4], size: 0.22, idle: "fly" },
      { item: "coins", at: [0.95, 0, -0.45], size: 0.18, idle: "none", cheers: false },
    ],
    light: { sky: "#F2FAFF", ground: "#3F7FB0", hemi: 1.5, key: "#FFF0D0", keyI: 1.3 },
    fog: { color: "#BFE6FF", near: 12, far: 36 },
    confetti: ["#FFD23F", "#FFC21A", "#4FB3E8", "#FFFFFF", "#E8414F"],
  },
  "zoo-stage": {
    key: "zoo-stage",
    sky: { top: "#58A9E0", horizon: "#FFF1C4", bottom: "#FFF7DD" },
    sun: { elev: 55, azim: 15, color: "#FFF1B8", size: 1.2 },
    clouds: { count: 6, color: "#FFFFFF" },
    ground: { color: "#D9B98A", color2: "#C9A673", pattern: "sand" },
    deck: "#E6C990",
    podium: {
      side: "#F4A261",
      top: "#FFD166",
      dots: "#FFF4C4",
      bulbs: ["#7BD389", "#FFFFFF"],
      skirt: ["#7BD389", "#FFD166"],
      pattern: "dots",
    },
    far: [
      { kind: "hills", color: "#A6C97F", dist: 24, height: 2.8, seed: 8 },
      { kind: "forest", color: "#5D9A4E", dist: 16, height: 3, seed: 13 },
    ],
    props: [
      { kind: "palm", at: [-1.65, -1.8], scale: 1.4 },
      { kind: "palm", at: [1.8, -2.1], scale: 1.6 },
      { kind: "bush", at: [-0.98, -1.0] },
      { kind: "bush", at: [1.05, -0.9], scale: 1.2 },
      { kind: "fence", at: [0.0, -1.6], scale: 2.4 },
      { kind: "rock", at: [-1.2, -0.4] },
    ],
    figures: [
      { item: "banana", at: [-0.45, 0.06, 0.25], size: 0.14, idle: "none", cheers: false },
      { char: "adult", at: [0.95, 0, -0.45], size: 0.36, idle: "sway" },
      { char: "girl", at: [1.2, 0, -0.35], size: 0.26, idle: "bob" },
      { char: "kid", at: [-0.95, 0, -0.45], size: 0.3, idle: "bob" },
      { char: "giraffe", at: [-2.5, 0, -2.9], size: 1.1, idle: "sway" },
      { char: "elephant", at: [2.6, 0, -3.1], size: 0.9, idle: "none" },
    ],
    light: { sky: "#FFFDF0", ground: "#C8A878", hemi: 1.5, key: "#FFF0D0", keyI: 1.3 },
    fog: { color: "#FFF1C4", near: 11, far: 34 },
    confetti: ["#FFE14D", "#7BD389", "#FF9F1C", "#4FB3E8", "#FF5A8A"],
  },
  "sunset-sky": {
    key: "sunset-sky",
    sky: { top: "#3B3F8F", horizon: "#FF9A62", bottom: "#FFC89A" },
    sun: { elev: 7, azim: -10, color: "#FFB45E", size: 2.6 },
    clouds: { count: 8, color: "#FFB7A0" },
    ground: { color: "#7E8F6A", color2: "#6C7C5C", pattern: "grass" },
    deck: "#D9B88F",
    podium: {
      side: "#D97B6C",
      top: "#F7B2A0",
      dots: "#FFE3A3",
      bulbs: ["#FFE3A3", "#FFFFFF"],
      skirt: ["#FFB45E", "#C77DFF"],
      pattern: "stars",
    },
    far: [
      { kind: "hills", color: "#5B4E7A", dist: 26, height: 3, seed: 6 },
      { kind: "city", color: "#3E3560", dist: 19, height: 4.2, seed: 21, lights: "#FFD27A" },
    ],
    props: [
      { kind: "lamp", at: [-1.05, -1.2] },
      { kind: "lamp", at: [1.05, -1.2] },
      { kind: "tree", at: [-1.72, -1.9], scale: 1.5, color: "#3F6B4A" },
      { kind: "bush", at: [1.5, -0.4] },
      { kind: "fence", at: [0.0, -2.2], scale: 2.2 },
    ],
    figures: [
      { char: "adult", at: [-0.95, 0, -0.45], size: 0.36, idle: "sway" },
      { char: "adult", color: "#4FB3E8", at: [0.95, 0, -0.5], size: 0.36, idle: "sway" },
    ],
    light: { sky: "#FFD3B0", ground: "#5A4A6A", hemi: 1.2, key: "#FFB878", keyI: 1.4 },
    fog: { color: "#FF9A62", near: 10, far: 32 },
    confetti: ["#FFB45E", "#FF8FB1", "#FFD23F", "#C77DFF", "#FFFFFF"],
  },
  "drop-tower": {
    key: "drop-tower",
    sky: { top: "#2D7FD8", horizon: "#C2E4FF", bottom: "#E2F2FF" },
    sun: { elev: 60, azim: 40, color: "#FFF6C8", size: 1.2 },
    clouds: { count: 9, color: "#FFFFFF" },
    ground: { color: "#D8D2C4", color2: "#C7BFAE", pattern: "tile" },
    deck: "#FFD166",
    podium: {
      side: "#3DBE5A",
      top: "#7BD389",
      dots: "#FFFFFF",
      bulbs: ["#FFFFFF", "#FFD166"],
      skirt: ["#4FB3E8", "#FFD166"],
      pattern: "stars",
    },
    far: [...FAIR_FAR, { kind: "fair", color: "#5C6FA0", dist: 14, height: 7, seed: 31 }],
    props: [
      { kind: "tower", at: [-1.95, -2.8], scale: 1.6, color: "#E8414F" },
      { kind: "bunting", at: [-1.6, -1.1], to: [1.6, -1.1] },
      { kind: "balloons", at: [1.35, 0.2], color: "#FF6B6B" },
      { kind: "lamp", at: [0.98, -1.7] },
      { kind: "stand", at: [1.65, -1.2], color: "#7BD389" },
    ],
    figures: [
      { char: "kid", color: "#FFD23F", at: [-0.9, 0, -0.4], size: 0.3, idle: "bob" },
      { char: "girl", color: "#C77DFF", at: [0.9, 0, -0.45], size: 0.3, idle: "bob" },
      { char: "adult", color: "#FF9F1C", at: [-0.6, 0, -0.65], size: 0.32, idle: "sway" },
    ],
    light: { sky: "#FFFFFF", ground: "#BDB5A4", hemi: 1.5, key: "#FFF1D6", keyI: 1.3 },
    fog: { color: "#C2E4FF", near: 12, far: 36 },
    confetti: PARTY,
  },
  "cake-shop": {
    key: "cake-shop",
    indoor: true,
    sky: { top: "#F7C9D9", horizon: "#FFE6EE", bottom: "#FFF3F6" },
    ground: { color: "#FFFFFF", color2: "#F6C9D7", pattern: "tile" },
    deck: "#C99A6B",
    podium: {
      side: "#FF8FB1",
      top: "#FFE4EC",
      dots: "#FFFFFF",
      bulbs: ["#FFFFFF", "#FFD166"],
      skirt: ["#FFFFFF", "#FF8FB1"],
      pattern: "hearts",
    },
    far: [{ kind: "wall", color: "#FFD3E0", dist: 6, height: 3.2, seed: 41, lights: "#FFF4C4" }],
    props: [
      { kind: "cake", at: [-0.98, -1.0], scale: 1.1, color: "#FFC1D6" },
      { kind: "cake", at: [1.05, -1.1], scale: 0.9, color: "#FFE8A3" },
      { kind: "cake", at: [0.0, -1.9], scale: 1.4, color: "#FFFFFF" },
      { kind: "stand", at: [-1.58, -0.2], color: "#FF8FAB" },
      { kind: "lamp", at: [1.58, -0.4] },
    ],
    figures: [
      { char: "chef", at: [0.9, 0, -0.4], size: 0.36, idle: "sway" },
      { item: "cupcake", at: [-0.85, 0, -0.45], size: 0.16, idle: "bob", cheers: false },
      { item: "strawberry", at: [-0.6, 0, -0.6], size: 0.12, idle: "bob", cheers: false },
      { item: "milk", at: [0.55, 0, -0.65], size: 0.14, idle: "none", cheers: false },
    ],
    light: { sky: "#FFF6F8", ground: "#E9C3CF", hemi: 1.6, key: "#FFF1E6", keyI: 1.0 },
    fog: { color: "#FFE6EE", near: 7, far: 16 },
    confetti: ["#FF8FB1", "#FFFFFF", "#E5304A", "#FFE8A3", "#7FD3F7"],
  },
  "mountain-heli": {
    key: "mountain-heli",
    sky: { top: "#2E7BD1", horizon: "#CFE6FA", bottom: "#E8F3FC" },
    sun: { elev: 45, azim: -30, color: "#FFF8DC", size: 1.1 },
    clouds: { count: 10, color: "#FFFFFF" },
    ground: { color: "#8C9A7A", color2: "#7A876A", pattern: "rock" },
    deck: "#59626E",
    podium: {
      side: "#FF7A00",
      top: "#FFD166",
      dots: "#FFFFFF",
      bulbs: ["#FFFFFF", "#4FB3E8"],
      skirt: ["#FF7A00", "#FFFFFF"],
      pattern: "stars",
    },
    far: [
      { kind: "mountains", color: "#7F95B2", dist: 28, height: 9, seed: 17, snow: true },
      { kind: "mountains", color: "#5E7F6E", dist: 20, height: 5, seed: 23 },
      { kind: "forest", color: "#335E46", dist: 14, height: 2.6, seed: 29 },
    ],
    props: [
      { kind: "helipad", at: [0.0, 0.0], scale: 1 },
      { kind: "pine", at: [-1.5, -1.6], scale: 1.6 },
      { kind: "pine", at: [1.72, -1.9], scale: 1.9 },
      { kind: "rock", at: [-0.98, -0.9], scale: 1.3 },
      { kind: "rock", at: [1.2, 0.3] },
      { kind: "flag", at: [0.9, -1.2], color: "#FF7A00" },
    ],
    figures: [
      { char: "rescuer", at: [0.95, 0, -0.4], size: 0.34, idle: "sway" },
      { item: "helmet", at: [-0.8, 0, -0.45], size: 0.16, idle: "none", cheers: false },
      { item: "helicopter", at: [-3.0, 2.2, -4.5], size: 0.5, idle: "fly", cheers: false },
    ],
    light: { sky: "#FFFFFF", ground: "#7A876A", hemi: 1.5, key: "#FFF6E0", keyI: 1.3 },
    fog: { color: "#CFE6FA", near: 12, far: 40 },
    confetti: ["#FF7A00", "#FFD23F", "#4FB3E8", "#FFFFFF", "#7BD389"],
  },
  "forest-concert": {
    key: "forest-concert",
    sky: { top: "#1E2459", horizon: "#7C5BA6", bottom: "#B892C9" },
    stars: true,
    ground: { color: "#3E6B48", color2: "#335C3C", pattern: "moss" },
    deck: "#A0784E",
    podium: {
      side: "#6A4C9C",
      top: "#9B72CF",
      dots: "#FFD23F",
      bulbs: ["#FFD23F", "#BDE7FF"],
      skirt: ["#FFD23F", "#9B72CF"],
      pattern: "stars",
    },
    far: [
      { kind: "hills", color: "#2E2E5C", dist: 25, height: 3.2, seed: 33 },
      { kind: "forest", color: "#1F3B33", dist: 15, height: 3.8, seed: 37 },
    ],
    props: [
      { kind: "lamp", at: [-0.9, -0.8] },
      { kind: "lamp", at: [0.9, -0.8] },
      { kind: "lamp", at: [-1.42, 0.2] },
      { kind: "lamp", at: [1.42, 0.2] },
      { kind: "pine", at: [-1.8, -1.8], scale: 1.8 },
      { kind: "pine", at: [1.88, -2.0], scale: 1.6 },
      { kind: "mushroom", at: [-0.75, -1.5], color: "#E5483B" },
      { kind: "mushroom", at: [0.98, -1.6], color: "#4FB3E8", scale: 0.8 },
    ],
    figures: [
      { char: "fox", at: [-1.0, 0, -0.35], size: 0.26, idle: "bob" },
      { char: "rabbit", at: [-0.65, 0, -0.6], size: 0.24, idle: "bob" },
      { char: "squirrel", at: [0.65, 0, -0.6], size: 0.22, idle: "bob" },
      { char: "hedgehog", at: [1.0, 0, -0.35], size: 0.22, idle: "bob" },
      { char: "owl", at: [-2.4, 1.3, -1.7], size: 0.26, idle: "none" },
    ],
    light: { sky: "#C9B8FF", ground: "#2A3B3A", hemi: 1.1, key: "#FFE7B8", keyI: 1.0 },
    fog: { color: "#7C5BA6", near: 9, far: 28 },
    confetti: ["#FFD23F", "#C9B8FF", "#7FD3F7", "#FF8FB1", "#FFFFFF"],
  },
  "forest-nest": {
    key: "forest-nest",
    sky: { top: "#64B6EC", horizon: "#FFF0D2", bottom: "#FFF8E8" },
    sun: { elev: 22, azim: 35, color: "#FFE9A8", size: 1.5 },
    clouds: { count: 8, color: "#FFFFFF" },
    ground: { color: "#86C66F", color2: "#71B05C", pattern: "grass" },
    deck: "#C8A27A",
    podium: {
      side: "#7BD389",
      top: "#BFF2D6",
      dots: "#FFFFFF",
      bulbs: ["#FF9EC4", "#FFFFFF"],
      skirt: ["#FF9EC4", "#FFD166"],
      pattern: "dots",
    },
    far: [
      { kind: "hills", color: "#A9D69A", dist: 25, height: 2.6, seed: 43 },
      { kind: "forest", color: "#4F9A5E", dist: 16, height: 3.2, seed: 47 },
    ],
    props: [
      { kind: "rainbow", at: [0.0, -9.0], scale: 5 },
      { kind: "tree", at: [-1.42, -1.4], scale: 2.0 },
      { kind: "nest", at: [-1.42, -1.4], scale: 1 },
      { kind: "flowers", at: [0.9, -0.8], color: "#FF8FB1" },
      { kind: "flowers", at: [-0.68, -0.9], color: "#FFD166" },
      { kind: "bush", at: [1.5, -1.2] },
    ],
    figures: [
      { char: "chick", at: [0.45, 0.06, 0.15], size: 0.16, idle: "bob" },
      { char: "bird", at: [-1.9, 1.75, -1.4], size: 0.24, idle: "sway" },
      { char: "chick", at: [-1.75, 1.6, -1.25], size: 0.14, idle: "bob" },
      { item: "butterfly", at: [1.0, 0.6, -0.3], size: 0.14, idle: "fly", cheers: false },
    ],
    light: { sky: "#FFFFFF", ground: "#86B06F", hemi: 1.5, key: "#FFEFC8", keyI: 1.3 },
    fog: { color: "#FFF0D2", near: 12, far: 34 },
    confetti: ["#FF5A5F", "#FFD166", "#7BD389", "#4FB3E8", "#9B72CF"],
  },
  "dino-museum": {
    key: "dino-museum",
    indoor: true,
    sky: { top: "#B9AE9A", horizon: "#E8E0D0", bottom: "#F2ECE0" },
    ground: { color: "#CFC3AE", color2: "#BCAF97", pattern: "tile" },
    deck: "#7A6A55",
    podium: {
      side: "#A88B63",
      top: "#D9C7A7",
      dots: "#F4ECD8",
      bulbs: ["#FFD166", "#FFFFFF"],
      skirt: ["#6FCF7A", "#FFD166"],
      pattern: "dots",
    },
    far: [{ kind: "wall", color: "#E2D8C4", dist: 7, height: 3.8, seed: 51, lights: "#FFF2C8" }],
    props: [
      { kind: "column", at: [-1.5, -1.6], scale: 1.4 },
      { kind: "column", at: [1.5, -1.6], scale: 1.4 },
      { kind: "column", at: [-1.5, -0.6], scale: 1.4 },
      { kind: "column", at: [1.5, -0.6], scale: 1.4 },
      { kind: "fence", at: [0.0, -0.75], scale: 1.6 },
      { kind: "rock", at: [-0.9, -1.2], color: "#B8A98E" },
    ],
    figures: [
      { item: "skeleton", at: [-1.5, 0, -2.6], size: 1.2, idle: "none", cheers: false },
      { item: "bone", at: [1.3, 0, -1.4], size: 0.3, idle: "none", cheers: false },
      { char: "kid", at: [-0.9, 0, -0.85], size: 0.3, idle: "sway" },
      { char: "girl", at: [0.9, 0, -0.85], size: 0.3, idle: "sway" },
      { item: "camera", at: [0.6, 0.2, -0.9], size: 0.12, idle: "none", cheers: false },
    ],
    light: { sky: "#FFF8EC", ground: "#B0A48C", hemi: 1.5, key: "#FFF0D8", keyI: 1.1 },
    fog: { color: "#E8E0D0", near: 7, far: 18 },
    confetti: ["#FFD23F", "#6FCF7A", "#FF9F1C", "#FFFFFF", "#4FB3E8"],
  },
  // ── 정적 기계 무대(2026-10-07) — 로봇 팔 · 집게 · 프레스(공방) · 색상 분류기(공장) · 플로터 · 무늬(미술 교실) · 펀치 · 배구 · 탁구(운동회) ──
  "toy-workshop": {
    key: "toy-workshop",
    indoor: true,
    sky: { top: "#C9B79C", horizon: "#F3E6CF", bottom: "#FAF2E4" },
    ground: { color: "#D9B98C", color2: "#C9A777", pattern: "wood" },
    deck: "#8A6A48",
    podium: {
      side: "#4FB3E8",
      top: "#BFE4F7",
      dots: "#FFFFFF",
      bulbs: ["#FFD166", "#FFFFFF"],
      skirt: ["#FFD166", "#4FB3E8"],
      pattern: "planks",
    },
    far: [{ kind: "wall", color: "#EADBC1", dist: 6.5, height: 3.4, seed: 61, lights: "#FFF2C8" }],
    props: [
      { kind: "stand", at: [-1.5, -0.4], color: "#4FB3E8" },
      { kind: "stand", at: [1.5, -0.5], color: "#FFB547" },
      { kind: "lamp", at: [-1.62, -1.5] },
      { kind: "lamp", at: [1.62, -1.6] },
      { kind: "balloons", at: [0.0, -1.9], color: "#FF5A8A" },
      { kind: "bunting", at: [-1.3, -1.7], to: [1.3, -1.7] },
    ],
    figures: [
      { char: "teddy", at: [-0.95, 0, -0.5], size: 0.26, idle: "bob" },
      { char: "kid", at: [0.95, 0, -0.55], size: 0.3, idle: "sway" },
      { char: "adult", at: [0.0, 0, -1.25], size: 0.36, idle: "sway", color: "#4FB3E8" },
      { item: "helmet", at: [-0.6, 0, -0.7], size: 0.14, idle: "none", cheers: false },
      { item: "coins", at: [0.55, 0, -0.75], size: 0.14, idle: "bob", cheers: false },
    ],
    light: { sky: "#FFF8EC", ground: "#C9A777", hemi: 1.55, key: "#FFF0D8", keyI: 1.05 },
    fog: { color: "#F3E6CF", near: 7, far: 17 },
    confetti: ["#FFD23F", "#4FB3E8", "#FF5A8A", "#FFFFFF", "#7BD389"],
  },
  "candy-factory": {
    key: "candy-factory",
    indoor: true,
    sky: { top: "#C8B6F2", horizon: "#F1E9FF", bottom: "#FAF6FF" },
    ground: { color: "#FFFFFF", color2: "#E6DAFB", pattern: "tile" },
    deck: "#9C7FD6",
    podium: {
      side: "#FF8FB1",
      top: "#FFE1EC",
      dots: "#FFFFFF",
      bulbs: ["#FFD166", "#7FD3F7"],
      skirt: ["#FF8FB1", "#7FD3F7"],
      pattern: "dots",
    },
    far: [{ kind: "wall", color: "#E9DDFE", dist: 6.5, height: 3.4, seed: 67, lights: "#FFF4C4" }],
    props: [
      { kind: "cake", at: [-1.45, -1.0], scale: 0.9, color: "#FF8FB1" },
      { kind: "cake", at: [1.45, -1.1], scale: 0.9, color: "#7FD3F7" },
      { kind: "stand", at: [-1.55, -0.2], color: "#C77DFF" },
      { kind: "stand", at: [1.55, -0.25], color: "#FFB547" },
      { kind: "balloons", at: [-0.5, -1.9], color: "#C77DFF" },
      { kind: "balloons", at: [0.6, -1.95], color: "#FFD23F" },
    ],
    figures: [
      { char: "chef", at: [0.95, 0, -0.5], size: 0.36, idle: "sway" },
      { char: "girl", at: [-0.95, 0, -0.55], size: 0.3, idle: "bob" },
      { item: "icecream", at: [-0.6, 0, -0.75], size: 0.15, idle: "bob", cheers: false },
      { item: "cupcake", at: [0.55, 0, -0.78], size: 0.15, idle: "bob", cheers: false },
      { item: "strawberry", at: [0.0, 0, -1.1], size: 0.12, idle: "bob", cheers: false },
    ],
    light: { sky: "#FBF7FF", ground: "#D9C9F5", hemi: 1.6, key: "#FFF1F6", keyI: 1.0 },
    fog: { color: "#F1E9FF", near: 7, far: 16 },
    confetti: ["#FF8FB1", "#7FD3F7", "#C77DFF", "#FFD23F", "#FFFFFF"],
  },
  "art-studio": {
    key: "art-studio",
    indoor: true,
    sky: { top: "#A9D3E8", horizon: "#EAF6FB", bottom: "#F6FBFD" },
    ground: { color: "#E8D5B5", color2: "#D8C29E", pattern: "wood" },
    deck: "#6F8FA8",
    podium: {
      side: "#FFC93C",
      top: "#FFF1C2",
      dots: "#FF5A8A",
      bulbs: ["#FF5A8A", "#4FB3E8"],
      skirt: ["#7BD389", "#FFC93C"],
      pattern: "stars",
    },
    far: [{ kind: "wall", color: "#F4F0E6", dist: 6.5, height: 3.4, seed: 71, lights: "#FFF6D6" }],
    props: [
      { kind: "rainbow", at: [0.0, -2.1], scale: 1.4 },
      { kind: "flowers", at: [-1.45, -0.4], color: "#FF5A8A" },
      { kind: "flowers", at: [1.45, -0.45], color: "#4FB3E8" },
      { kind: "stand", at: [-1.55, -1.3], color: "#7BD389" },
      { kind: "stand", at: [1.55, -1.35], color: "#FF8FB1" },
      { kind: "lamp", at: [1.7, -0.2] },
    ],
    figures: [
      { char: "girl", at: [-0.95, 0, -0.5], size: 0.3, idle: "sway", color: "#FFC93C" },
      { char: "kid", at: [0.95, 0, -0.55], size: 0.3, idle: "bob", color: "#7BD389" },
      { char: "fox", at: [-0.45, 0, -1.15], size: 0.24, idle: "bob" },
      { item: "butterfly", at: [0.5, 0.45, -0.9], size: 0.12, idle: "fly", cheers: false },
      { item: "camera", at: [0.6, 0.2, -0.8], size: 0.12, idle: "none", cheers: false },
    ],
    light: { sky: "#FFFFFF", ground: "#D8C29E", hemi: 1.6, key: "#FFF6E6", keyI: 1.05 },
    fog: { color: "#EAF6FB", near: 7, far: 17 },
    confetti: ["#FF5A8A", "#4FB3E8", "#FFC93C", "#7BD389", "#C77DFF"],
  },
  "sports-day": {
    key: "sports-day",
    sky: DAY,
    sun: { elev: 55, azim: -25, color: "#FFF6C8", size: 1.2 },
    clouds: { count: 8, color: "#FFFFFF" },
    ground: { color: "#7CC46A", color2: "#6AB35A", pattern: "grass" },
    deck: "#D88A5A",
    podium: {
      side: "#E8414F",
      top: "#FFD1D5",
      dots: "#FFFFFF",
      bulbs: ["#FFD166", "#FFFFFF"],
      skirt: ["#3C8DDB", "#FFFFFF"],
      pattern: "stars",
    },
    far: [
      { kind: "hills", color: "#8FC79A", dist: 26, height: 3.2, seed: 13 },
      { kind: "city", color: "#9DB0C8", dist: 20, height: 5, seed: 19, lights: "#FFE9A8" },
    ],
    props: [
      { kind: "bunting", at: [-1.5, -1.2], to: [1.5, -1.2] },
      { kind: "flag", at: [-1.6, -0.3], color: "#E8414F" },
      { kind: "flag", at: [1.6, -0.35], color: "#3C8DDB" },
      { kind: "tent", at: [-1.4, -2.0], color: "#3C8DDB" },
      { kind: "tent", at: [1.5, -2.1], color: "#E8414F" },
      { kind: "tree", at: [0.0, -2.6], scale: 1.4 },
    ],
    figures: [
      { char: "kid", at: [-0.95, 0, -0.5], size: 0.3, idle: "bob", color: "#E8414F" },
      { char: "girl", at: [0.95, 0, -0.55], size: 0.3, idle: "bob", color: "#3C8DDB" },
      { char: "adult", at: [-0.5, 0, -1.2], size: 0.36, idle: "sway" },
      { char: "bear", at: [0.5, 0, -1.2], size: 0.28, idle: "sway" },
      { char: "bird", at: [0.3, 1.0, -1.4], size: 0.15, idle: "fly", cheers: false },
    ],
    light: { sky: "#FFFFFF", ground: "#8DBA78", hemi: 1.5, key: "#FFF1D6", keyI: 1.3 },
    fog: { color: "#CDEBFF", near: 10, far: 32 },
    confetti: ["#E8414F", "#3C8DDB", "#FFFFFF", "#FFD23F", "#7BD389"],
  },
};

/** 모르는 장면은 놀이터 낮(장면 키가 늘어도 그려진다) */
export function stageTheme(key: string | null | undefined): StageTheme {
  return (key && STAGE_THEMES[key]) || STAGE_THEMES["playground-sand"]!;
}
